import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, forkJoin } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export interface LeakSearchBatchResult {
  job_ids: string[];
  failed_count?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LeakSearchService {
  static readonly MAX_SEARCH_COUNT = 5;

  baseUrl: string = environment.baseUrl;

  constructor(private http: HttpClient) {}

  /** Normalize single or batch start-search responses into job id strings. */
  static extractJobIds(data: unknown): string[] {
    if (!data) {
      return [];
    }

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return [];
      }
      if (typeof data[0] === 'string') {
        return data.filter((id): id is string => Boolean(id));
      }
      return data
        .map((item: any) => item?.job_id)
        .filter((id): id is string => Boolean(id));
    }

    const record = data as LeakSearchBatchResult & { job_id?: string };
    if (Array.isArray(record.job_ids)) {
      return record.job_ids.filter(Boolean);
    }
    if (record.job_id) {
      return [record.job_id];
    }

    return [];
  }

  /**
   * Start one or more leak searches. Each query triggers a separate POST /leak/search
   * so credits and history remain per individual search on the backend.
   */
  getJobIds(
    type: string,
    searchValues: string[],
    ipAddress: string,
    location: string,
  ): Observable<any> {
    const uniqueValues = [
      ...new Set(
        searchValues.map((value) => value.trim()).filter((value) => value.length > 0),
      ),
    ];

    if (uniqueValues.length === 0) {
      return of({ data: { job_ids: [] as string[] } });
    }

    if (uniqueValues.length === 1) {
      return this.startSingleSearch(
        type,
        uniqueValues[0],
        ipAddress,
        location,
      );
    }

    return from(uniqueValues).pipe(
      concatMap((query) =>
        this.startSingleSearch(type, query, ipAddress, location).pipe(
          map((response) => ({
            ok: true as const,
            jobId: response?.data?.job_id as string | undefined,
          })),
          catchError(() => of({ ok: false as const, jobId: undefined })),
        ),
      ),
      toArray(),
      map((results) => {
        const jobIds = results
          .filter((result) => result.ok && result.jobId)
          .map((result) => result.jobId as string);
        const failedCount = results.length - jobIds.length;

        return {
          data: {
            job_ids: jobIds,
            ...(failedCount > 0 ? { failed_count: failedCount } : {}),
          },
        };
      }),
    );
  }

  /** Poll job status for one or many leak search jobs. */
  getSearchResults(type: string, jobIds: string[]): Observable<any> {
    const url = `${this.baseUrl}/leak/job`;
    const ids = (jobIds || []).filter(Boolean);

    if (ids.length === 0) {
      return of({ data: { jobs: [] } });
    }

    if (ids.length === 1) {
      const params = new HttpParams()
        .set('query_type', type)
        .set('job_id', ids[0]);

      return this.http.get(url, { params });
    }

    return forkJoin(
      ids.map((jobId) =>
        this.http.get(url, {
          params: new HttpParams()
            .set('query_type', type)
            .set('job_id', jobId),
        }),
      ),
    ).pipe(
      map((responses: any[]) => ({
        data: {
          jobs: responses.map((response, index) => {
            const done = response?.data?.done ?? false;
            return {
              job_id: ids[index],
              done,
              status: response?.data?.status ?? (done ? 'completed' : 'pending'),
              credit_refunded: response?.data?.credit_refunded ?? false,
            };
          }),
        },
      })),
    );
  }

  getCompletedSearchResult(type: string, searchId: string): Observable<any> {
    const url: string = `${this.baseUrl}/leak/results`;
    const params = new HttpParams()
      .set('leak_search_id', searchId)
      .set('type', type);

    return this.http.get(url, { params });
  }

  exportSearchResult(
    type: string,
    searchId: string,
    format: string,
  ): Observable<any> {
    const url: string = `${this.baseUrl}/leak/results`;
    const params = new HttpParams()
      .set('leak_search_id', searchId)
      .set('type', type)
      .set('is_export', true)
      .set('format', format);

    return this.http.get(url, { params });
  }

  updateNotes(resultId: string, payload: any): Observable<any> {
    const params = new HttpParams().set('result_id', resultId);

    return this.http.put(`${this.baseUrl}/leak/update`, payload, { params });
  }

  private startSingleSearch(
    type: string,
    query: string,
    ipAddress: string,
    location: string,
  ): Observable<any> {
    const payload: any = {
      query_type: type,
      query,
      match_type: 'exact',
      device: 'chrome',
    };

    if (ipAddress !== '') {
      payload.ip_address = ipAddress;
    }

    if (location !== '') {
      payload.location = location;
    }

    return this.http.post(`${this.baseUrl}/leak/search`, payload);
  }
}
